/**
 * Validated environment configuration.
 *
 * All env vars are validated at module load time — the app will fail fast
 * if required variables are missing in production.
 *
 * ## Adding a new env var
 *
 * 1. Add the raw var to `src/vite-env.d.ts`       (TypeScript type)
 * 2. Add the parsed field to `AppEnv` below        (typed interface)
 * 3. Add the reader in the `env` object below      (validated read)
 * 4. Add to `.env.example`                         (documentation + classification)
 * 5. Add to `.env`                                 (local dev value)
 * 6. Update tests in `env.spec.ts`                 (shape assertion)
 */

import { getRequiredEnv } from '@hetivo/hetivo-lib-web-utils';

export interface AppEnv {
  /** Base URL for API requests (no trailing slash) */
  apiBaseUrl: string;
  /** Azure AD tenant ID used to pin Microsoft SSO to a single tenant */
  azureTenantId: string;
  /** Firebase API key */
  firebaseApiKey: string;
  /** Firebase App ID */
  firebaseAppId: string;
  /** Firebase Auth Domain */
  firebaseAuthDomain: string;
  /** Firebase Messaging Sender ID */
  firebaseMessagingSenderId: string;
  /** Firebase Project ID */
  firebaseProjectId: string;
  /** Firebase Storage Bucket */
  firebaseStorageBucket: string;
  /** HERE Maps API key (used for map tiles) */
  hereApiKey: string;
  /** Whether the app is running in development mode */
  isDev: boolean;
  /** Whether the app is running in production mode */
  isProd: boolean;
}

export const env: AppEnv = {
  apiBaseUrl: getRequiredEnv('VITE_API_BASE_URL').replace(/\/$/, ''),
  azureTenantId: getRequiredEnv('VITE_AZURE_TENANT_ID'),
  firebaseApiKey: getRequiredEnv('VITE_FIREBASE_API_KEY'),
  firebaseAppId: getRequiredEnv('VITE_FIREBASE_APP_ID'),
  firebaseAuthDomain: getRequiredEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  firebaseMessagingSenderId: getRequiredEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  firebaseProjectId: getRequiredEnv('VITE_FIREBASE_PROJECT_ID'),
  firebaseStorageBucket: getRequiredEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  hereApiKey: getRequiredEnv('VITE_HERE_API_KEY'),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
};
